/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {rapatriementAgeGap} from '../../data/constants/Assurance/AssuranceRapatriement/souscription';
import {dataConfig} from '../../data/constants/Assurance/AssuranceVoyage/souscription';
import {ItemI} from '../../data/types/item';

export function addThousandSeparator(number: number): string {
  const numberString = number.toString();
  const decimalSeparatorIndex = numberString.indexOf('.');
  let integerPart = numberString;
  let decimalPart = '';

  // Separate integer and decimal parts, if applicable
  if (decimalSeparatorIndex !== -1) {
    integerPart = numberString.slice(0, decimalSeparatorIndex);
    decimalPart = numberString.slice(decimalSeparatorIndex);
  }

  // Add thousand separator (space) to the integer part
  integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  // Join integer and decimal parts, if applicable
  const formattedNumber = decimalPart ? integerPart + decimalPart : integerPart;

  return formattedNumber;
}

export function isBase64Image(base64String: string): boolean {
  const mimeTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp'];

  // Check if the Base64 string matches any of the supported MIME types
  const matchedMimeType = mimeTypes.find(mimeType => {
    return base64String.startsWith(`data:${mimeType};base64,`);
  });

  // If a matching MIME type is found, try to decode the image
  if (matchedMimeType) {
    const img: any = new Image();
    img.src = base64String;
    return img.decode ? true : false;
  }

  // If no matching MIME type is found, return false
  return false;
}

export function isAdult(birthdate: string): boolean {
  // Parse the birthdate string into a Day.js object
  const birthdateObj = dayjs(birthdate, 'DD/MM/YYYY');

  // Calculate the difference between the birthdate and the current date in years
  const age = dayjs().diff(birthdateObj, 'year');

  // Check if the person is an adult (i.e., age >= 18)
  return age >= 18;
}

export function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const findOldestDate = (
  array: Array<any>,
  propertyName = 'dateNaissance',
) => {
  let oldestDate: any = null;
  dayjs.extend(customParseFormat);
  array.forEach(obj => {
    const date: any = formatDateTo(obj[propertyName], 'dayjs');

    if (!oldestDate || date.isBefore(oldestDate)) {
      oldestDate = date;
    }
  });

  return oldestDate;
};

export const countPresonsInAgeRange = (
  array: Array<any>,
  ageLimits: {min: number; max: number} = {min: 0, max: 18},
  propertyName = 'dateNaissance',
) => {
  let count = 0;
  dayjs.extend(customParseFormat);

  array.forEach(obj => {
    const date = formatDateTo(obj[propertyName], 'Date');
    const age = dayjs().diff(date, 'year');

    if (age >= ageLimits.min && age < ageLimits.max) {
      count++;
    }
  });

  return count;
};

export const nbrAssureIsDifferentFromDevis = (
  response: any,
  listNaissance: any,
) => response.assure.length !== listNaissance.length;

export const assuredAgeIsDifferentFromDevis = (
  currentAges: {childrens: number; adults: number; seniors: number},
  oldAges: {childrens: number; adults: number; seniors: number},
) =>
  !(
    currentAges.childrens === oldAges.childrens &&
    currentAges.adults === oldAges.adults &&
    currentAges.seniors === oldAges.seniors
  );

interface AgeLimit {
  min: number;
  max: number;
}

interface AgeLimits {
  childrens: AgeLimit;
  adults: AgeLimit;
  seniors: AgeLimit;
}

interface CountResult {
  childrens: number;
  adults: number;
  seniors: number;
}

interface AgeRapatriementLimits {
  adults1: AgeLimit;
  adults2: AgeLimit;
  adults3: AgeLimit;
  adults4: AgeLimit;
  adults5: AgeLimit;
  adults6: AgeLimit;
}

interface CountRapatriementResult {
  adults1: number;
  adults2: number;
  adults3: number;
  adults4: number;
  adults5: number;
  adults6: number;
}

export type AgeRapatriementCategory = {
  adults1: number;
  adults2: number;
  adults3: number;
  adults4: number;
  adults5: number;
  adults6: number;
};

export const defaultAgeRangeVoyage = {
  childrens: {
    min: dataConfig.ageGap.enfant.min,
    max: dataConfig.ageGap.enfant.max,
  },
  adults: {min: dataConfig.ageGap.adult.min, max: dataConfig.ageGap.adult.max},
  seniors: {
    min: dataConfig.ageGap.senior.min,
    max: dataConfig.ageGap.senior.max,
  },
};

export const countPersonsInAgeRanges = (
  array: Array<any>,
  ageLimits: AgeLimits = {
    childrens: {min: 0, max: 18},
    adults: {min: 18, max: 60},
    seniors: {min: 60, max: 150},
  },
  propertyName = 'dateNaissance',
  format?: string | Array<string>,
): CountResult => {
  const counts: CountResult = {
    childrens: 0,
    adults: 0,
    seniors: 0,
  };

  array.forEach(obj => {
    const dateValue = obj[propertyName];

    if (!dateValue) return; // Skip if the property doesn't exist

    let date: dayjs.Dayjs;

    if (format) {
      date = dayjs(dateValue, format);
    } else {
      date = dayjs(dateValue);
    }

    if (!date.isValid()) return; // Skip invalid dates

    const age = dayjs().diff(date, 'year');

    if (age >= ageLimits.childrens.min && age < ageLimits.childrens.max) {
      counts.childrens++;
    } else if (age >= ageLimits.adults.min && age < ageLimits.adults.max) {
      counts.adults++;
    } else if (age >= ageLimits.seniors.min && age < ageLimits.seniors.max) {
      counts.seniors++;
    }
  });

  return counts;
};

export const countPersonsRapatriementInAgeRanges = (
  array: Array<any>,
  ageLimits: AgeRapatriementLimits = rapatriementAgeGap,
  propertyName = 'dateNaissance',
  format?: string | Array<string>,
): CountRapatriementResult => {
  const counts: CountRapatriementResult = {
    adults1: 0,
    adults2: 0,
    adults3: 0,
    adults4: 0,
    adults5: 0,
    adults6: 0,
  };

  array.forEach(obj => {
    const dateValue = obj[propertyName];

    if (!dateValue) return; // Skip if the property doesn't exist

    let date: dayjs.Dayjs;

    if (format) {
      date = dayjs(dateValue, format);
    } else {
      date = dayjs(dateValue);
    }

    if (!date.isValid()) return; // Skip invalid dates

    const age = dayjs().diff(date, 'year');

    for (const key of Object.keys(
      ageLimits,
    ) as (keyof AgeRapatriementLimits)[]) {
      const {min, max} = ageLimits[key];
      if (age >= min && age <= max) {
        counts[key]++;
      }
    }
  });

  return counts;
};

export const assuredRapatriementAgeIsDifferentFromDevis = (
  currentAges: {
    adults1: number;
    adults2: number;
    adults3: number;
    adults4: number;
    adults5: number;
    adults6: number;
  },
  oldAges: {
    adults1: number;
    adults2: number;
    adults3: number;
    adults4: number;
    adults5: number;
    adults6: number;
  },
) =>
  !(
    currentAges.adults1 === oldAges.adults1 &&
    currentAges.adults2 === oldAges.adults2 &&
    currentAges.adults3 === oldAges.adults3 &&
    currentAges.adults4 === oldAges.adults4 &&
    currentAges.adults5 === oldAges.adults5 &&
    currentAges.adults6 === oldAges.adults6
  );

export function decodeStringWithAccents(
  encodedString: string | undefined = undefined,
) {
  if (encodedString === '' || !encodedString) {
    return '';
  }
  const decodingMap = {
    'Ã€': 'À',
    'Ã‚': 'Â',
    Ãƒ: 'Ã',
    'Ã„': 'Ä',
    'Ã…': 'Å',
    'Ã†': 'Æ',
    'Ã‡': 'Ç',
    Ãˆ: 'È',
    'Ã‰': 'É',
    ÃŠ: 'Ê',
    'Ã‹': 'Ë',
    ÃŒ: 'Ì',
    ÃŽ: 'Î',
    'Ã‘': 'Ñ',
    'Ã’': 'Ò',
    'Ã“': 'Ó',
    'Ã”': 'Ô',
    'Ã•': 'Õ',
    'Ã–': 'Ö',
    'Ã—': '×',
    'Ã˜': 'Ø',
    'Ã™': 'Ù',
    Ãš: 'Ú',
    'Ã›': 'Û',
    Ãœ: 'Ü',
    Ãž: 'Þ',
    ÃŸ: 'ß',
    'Ã ': 'à',
    'Ã¡': 'á',
    'Ã¢': 'â',
    'Ã£': 'ã',
    'Ã¤': 'ä',
    'Ã¥': 'å',
    'Ã¦': 'æ',
    'Ã§': 'ç',
    'Ã¨': 'è',
    'Ã©': 'é',
    Ãª: 'ê',
    'Ã«': 'ë',
    'Ã¬': 'ì',
    'Ã­': 'í',
    'Ã®': 'î',
    'Ã¯': 'ï',
    'Ã°': 'ð',
    'Ã±': 'ñ',
    'Ã²': 'ò',
    'Ã³': 'ó',
    'Ã´': 'ô',
    Ãµ: 'õ',
    'Ã¶': 'ö',
    'Ã·': '÷',
    'Ã¸': 'ø',
    'Ã¹': 'ù',
    Ãº: 'ú',
    'Ã»': 'û',
    'Ã¼': 'ü',
    'Ã½': 'ý',
    'Ã¾': 'þ',
    'Ã¿': 'ÿ',
  };
  let decodedString = encodedString;
  Object.entries(decodingMap).forEach(([encoded, decoded]) => {
    const regex = new RegExp(encoded, 'g');
    decodedString = decodedString.replace(regex, decoded);
  });

  return decodedString;
}

// Define the character replacement rules
const rules: {[key: string]: string} = {
  a: 'àáâãäå',
  A: 'ÀÁÂ',
  e: 'èéêë',
  E: 'ÈÉÊË',
  i: 'ìíîï',
  I: 'ÌÍÎÏ',
  o: 'òóôõöø',
  O: 'ÒÓÔÕÖØ',
  u: 'ùúûü',
  U: 'ÙÚÛÜ',
  y: 'ÿ',
  c: 'ç',
  C: 'Ç',
  n: 'ñ',
  N: 'Ñ',
};

// Get the key corresponding to a given special character
function getJSONKey(char: string): string | undefined {
  for (const key in rules) {
    if (rules[key].includes(char)) {
      return key;
    }
  }
}

// Replace special characters in a given text based on the rules
export function replaceSpecialCharacters(text: string): string {
  let regString = '';
  for (const key in rules) {
    regString += rules[key];
  }
  const regex = new RegExp(`[${regString}]`, 'g');
  return text.replace(regex, char => {
    return getJSONKey(char) || '';
  });
}

export function formatDateTo(
  dateToFormat: string | Date,
  destinationFormat = 'YYYY-MM-DD',
) {
  if (dayjs(dateToFormat, 'YYYY-MM-DD').isValid()) {
    return destinationFormat === 'Date'
      ? dayjs(dateToFormat).toDate()
      : destinationFormat === 'dayjs'
      ? dayjs(dateToFormat)
      : dayjs(dateToFormat).format(destinationFormat);
  } else if (dayjs(dateToFormat, 'DD/MM/YYYY').isValid()) {
    return destinationFormat === 'Date'
      ? dayjs(dateToFormat, 'DD/MM/YYYY').toDate()
      : destinationFormat === 'dayjs'
      ? dayjs(dateToFormat, 'DD/MM/YYYY')
      : dayjs(dateToFormat, 'DD/MM/YYYY').format(destinationFormat);
  } else if (dayjs(dateToFormat).isValid()) {
    return destinationFormat === 'Date'
      ? dayjs(dateToFormat).toDate()
      : destinationFormat === 'dayjs'
      ? dayjs(dateToFormat)
      : dayjs(dateToFormat).format(destinationFormat);
  } else if (dayjs(dateToFormat, '[date]').isValid()) {
    return destinationFormat === 'Date'
      ? dayjs(dateToFormat, '[date]').toDate()
      : destinationFormat === 'dayjs'
      ? dayjs(dateToFormat, '[date]')
      : dayjs(dateToFormat, '[date]').format(destinationFormat);
  } else {
    return destinationFormat === 'Date'
      ? dayjs(dateToFormat).toDate()
      : destinationFormat === 'dayjs'
      ? dayjs(dateToFormat)
      : dayjs(dateToFormat).format(destinationFormat);
  }
}

export const getBase64 = (file: File, cb: (base64Data: string) => void) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = function () {
    cb(reader.result as string);
  };
  reader.onerror = function (error) {
    console.log('Error: ', error);
  };
};

export function decodeJWT(jwtToken: string): {[key: string]: any} | null {
  const base64Url = jwtToken.split('.')[1];
  if (!base64Url) return null;

  try {
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const decodedData = atob(base64);
    const decodedObject = JSON.parse(decodedData);
    return decodedObject;
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

export type AgeCategory = {
  childrens: number;
  adults: number;
  seniors: number;
};
export function categorizeAges(birthDates: ItemI[]): AgeCategory {
  const today = new Date();
  const ageCategory: AgeCategory = {childrens: 0, adults: 0, seniors: 0};

  birthDates.forEach(birthDate => {
    let age = today.getFullYear() - birthDate.value.getFullYear();
    const monthDifference = today.getMonth() - birthDate.value.getMonth();
    const dayDifference = today.getDate() - birthDate.value.getDate();

    // Adjust age if the birth date hasn't occurred yet this year
    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
      age--;
    }

    if (age < 18) {
      ageCategory.childrens++;
    } else if (age <= 60) {
      ageCategory.adults++;
    } else {
      ageCategory.seniors++;
    }
  });

  return ageCategory;
}

export const getOldestDate = (dates: Date[]): Date | null => {
  if (dates.length === 0) return null; // Handle case for empty array

  return dates.reduce((oldest, current) =>
    current < oldest ? current : oldest,
  );
};
